import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import { useTheme } from "@/context/ThemeContext";
import { STAGES, PRIORIDADES, fmtDate, slaInfo } from "@/lib/constants";
import { Page, TicketStats, StageBadge, PrioBadge, Empty, SlaBadge, ChartCanvas, useNow, stageBg, pulseClass } from "@/components/ui-z";
import { useTicketActions } from "@/components/TicketActions";

const STAGE_ICON = { abertura: "fas fa-inbox", t1_triagem: "fas fa-filter", t2_atendimento_interno: "fas fa-headset", t3_responsavel_tecnico: "fas fa-tools", resolucao: "fas fa-check" };
const PRIO_COLORS = ["#6c757d", "#007bff", "#fd7e14", "#dc3545"];

export default function Dashboard() {
  const { tickets } = useStore();
  const { skin } = useTheme();
  const now = useNow();
  const actions = useTicketActions();
  const abertos = tickets.filter((t) => t.status === "aberto");
  const resolvidos = tickets.length - abertos.length;
  const risco = abertos.filter((t) => ["warning", "breached"].includes(slaInfo(t, now).state)).sort((a, b) => slaInfo(a, now).left - slaInfo(b, now).left);
  const atrasados = risco.filter((t) => slaInfo(t, now).state === "breached").length;
  const stageCounts = STAGES.map((s) => tickets.filter((t) => t.stage === s.key).length);
  const stageRisk = STAGES.map((s) => risco.filter((t) => t.stage === s.key).length);
  const prioCounts = PRIORIDADES.map((p) => tickets.filter((t) => t.prioridade === p.key).length);
  const taxa = tickets.length ? Math.round((resolvidos / tickets.length) * 100) : 0;

  // Mesmas opções do gráfico "Sales" do dashboard3.js do AdminLTE
  const ticksStyle = useMemo(() => ({ fontColor: skin.darkMode ? "#adb5bd" : "#495057", fontStyle: "bold" }), [skin.darkMode]);
  const barConfig = useMemo(() => ({
    type: "bar",
    data: {
      labels: STAGES.map((s) => s.short),
      datasets: [
        { label: "Chamados", backgroundColor: "#007bff", borderColor: "#007bff", data: stageCounts },
        { label: "Prazo em risco", backgroundColor: "#ced4da", borderColor: "#ced4da", data: stageRisk },
      ],
    },
    options: {
      maintainAspectRatio: false,
      tooltips: { mode: "index", intersect: true },
      hover: { mode: "index", intersect: true },
      legend: { display: false },
      scales: {
        yAxes: [{ gridLines: { display: true, lineWidth: "4px", color: skin.darkMode ? "rgba(255, 255, 255, .1)" : "rgba(0, 0, 0, .2)", zeroLineColor: "transparent" }, ticks: { beginAtZero: true, precision: 0, ...ticksStyle } }],
        xAxes: [{ display: true, gridLines: { display: false }, ticks: ticksStyle }],
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [stageCounts.join(), stageRisk.join(), ticksStyle, skin.darkMode]);

  // Mesmas opções do "Donut Chart" de pages/charts/chartjs.html
  const donutConfig = useMemo(() => ({
    type: "doughnut",
    data: { labels: PRIORIDADES.map((p) => p.label), datasets: [{ data: prioCounts, backgroundColor: PRIO_COLORS }] },
    options: { maintainAspectRatio: false, responsive: true, legend: { labels: { fontColor: ticksStyle.fontColor } } },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [prioCounts.join(), ticksStyle]);

  return (
    <Page title="Dashboard" crumbs={[["Dashboard"]]}>
      {/* Small boxes (Stat box) */}
      <TicketStats tickets={tickets} now={now} />

      {/* Fluxo de atendimento (Info boxes) */}
      <div className="row" data-testid="flow-card">
        {STAGES.map((s, i) => (
          <div key={s.key} className="col-12 col-sm-6 col-md">
            <Link to={`/chamados?stage=${s.key}`} className="text-reset" data-testid={`flow-stage-${s.key}`}>
              <div className="info-box">
                <span className={`info-box-icon ${stageBg(s.key)} elevation-1`}><i className={STAGE_ICON[s.key]} /></span>
                <div className="info-box-content">
                  <span className="info-box-text">Etapa {i + 1} &middot; {s.short}</span>
                  <span className="info-box-number">{stageCounts[i]}</span>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>

      <div className="row">
        <div className="col-lg-8">
          <div className="card" data-testid="chart-stage">
            <div className="card-header border-0">
              <div className="d-flex justify-content-between">
                <h3 className="card-title">Chamados por nível</h3>
                <Link to="/relatorios">Ver relatórios</Link>
              </div>
            </div>
            <div className="card-body">
              <div className="d-flex">
                <p className="d-flex flex-column">
                  <span className="text-bold text-lg">{tickets.length}</span>
                  <span>Chamados registrados</span>
                </p>
                <p className="ml-auto d-flex flex-column text-right">
                  <span className="text-success"><i className="fas fa-arrow-up" /> {taxa}%</span>
                  <span className="text-muted">Taxa de resolução</span>
                </p>
              </div>
              <ChartCanvas config={barConfig} height={200} />
              <div className="d-flex flex-row justify-content-end">
                <span className="mr-2"><i className="fas fa-square text-primary" /> Chamados</span>
                <span><i className="fas fa-square text-gray" /> Prazo em risco</span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card card-danger" data-testid="chart-priority">
            <div className="card-header">
              <h3 className="card-title">Prioridade</h3>
              <div className="card-tools">
                <button type="button" className="btn btn-tool" data-card-widget="collapse"><i className="fas fa-minus" /></button>
              </div>
            </div>
            <div className="card-body">
              {tickets.length === 0 ? <Empty text="Sem dados ainda" /> : <ChartCanvas config={donutConfig} height={250} />}
            </div>
          </div>
        </div>
      </div>

      {risco.length > 0 && (
        <div className="card" data-testid="sla-alerts-card">
          <div className="card-header border-0">
            <h3 className="card-title"><i className="fas fa-stopwatch mr-1" />Alertas de prazo</h3>
            <div className="card-tools">
              <Link to="/chamados?sla=breached" data-testid="sla-alerts-see-breached" className="btn btn-tool btn-sm"><span className="badge badge-danger">{atrasados} atrasado(s)</span></Link>
            </div>
          </div>
          <div className="card-body table-responsive p-0">
            <table className="table table-striped table-valign-middle" data-testid="sla-alerts-list">
              <thead><tr><th>Código</th><th>Título</th><th>Prioridade</th><th>Responsável</th><th>Prazo</th></tr></thead>
              <tbody>
                {risco.slice(0, 6).map((t) => (
                  <tr key={t.id} data-testid={`sla-alert-${t.codigo}`} onContextMenu={(e) => actions.open(t, e)}>
                    <td><b>{t.codigo}</b></td>
                    <td>{t.titulo}</td>
                    <td><PrioBadge prio={t.prioridade} /></td>
                    <td>{t.assigned_to_name || <span className="text-muted">sem responsável</span>}</td>
                    <td><SlaBadge ticket={t} now={now} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card" data-testid="recent-tickets">
        <div className="card-header border-0">
          <h3 className="card-title">Chamados recentes</h3>
          <div className="card-tools">
            <Link to="/chamados" className="btn btn-tool btn-sm"><i className="fas fa-bars" /></Link>
          </div>
        </div>
        <div className="card-body table-responsive p-0">
          {tickets.length === 0 ? (
            <Empty testId="dashboard-empty" text="Nenhum chamado registrado. Abra o primeiro chamado para começar." />
          ) : (
            <table className="table table-striped table-valign-middle">
              <thead><tr><th>Código</th><th>Título</th><th>Solicitante</th><th>Etapa</th><th>Prioridade</th><th>Prazo</th><th>Aberto em</th></tr></thead>
              <tbody>
                {tickets.slice(0, 8).map((t) => (
                  <tr key={t.id} className={pulseClass(t)} onContextMenu={(e) => actions.open(t, e)}>
                    <td><Link to={`/chamados/${t.id}`}><b>{t.codigo}</b></Link></td>
                    <td>{t.titulo}</td>
                    <td>{t.solicitante_nome}</td>
                    <td><StageBadge stage={t.stage} /></td>
                    <td><PrioBadge prio={t.prioridade} /></td>
                    <td><SlaBadge ticket={t} now={now} /></td>
                    <td>{fmtDate(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {actions.element}
    </Page>
  );
}
